// Dictionary — the 856's own segments: BSN (beginning segment), HL (the
// hierarchical level that drives the Shipment/Order/Pack/Tare/Item tree),
// TD1 (packaging/weight), PRF (purchase order reference), MAN (marks and
// numbers) and SN1 (item detail). TD5 (routing) is defined here too but with
// a loop-agnostic LoopId.Header, since the 855 header reuses it. Generic X12
// 856 (5010) definitions only; not tied to any trading partner's
// implementation guide.
//
// LIN, PID, REF, PER, N1-N6, FOB, DTM and CTT also appear in the 856 but are
// already defined in dictionary/header-segments.ts, dictionary/item-loop-
// segments.ts and dictionary/852-item-activity-segments.ts and merged in by
// index.ts — X12 defines one segment per id, so they are not redefined here.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {TRANSACTION_SET_PURPOSE_CODES} from './transaction-set-purpose-codes';
import {IDENTIFICATION_CODE_QUALIFIER_CODES} from './identification-code-qualifiers';
import {UNIT_OR_BASIS_FOR_MEASUREMENT_CODES} from './unit-or-basis-for-measurement-codes';
import {
  HIERARCHICAL_STRUCTURE_CODES,
  HIERARCHICAL_LEVEL_CODES,
  HIERARCHICAL_CHILD_CODES,
} from './hierarchical-codes';
import {PACKAGING_CODES, WEIGHT_QUALIFIER_CODES} from './packaging-codes';
import {TRANSPORTATION_METHOD_CODES} from './transportation-method-codes';
import {MARKS_AND_NUMBERS_QUALIFIER_CODES} from './marks-and-numbers-qualifiers';
import {LINE_ITEM_STATUS_CODES} from './line-item-status-codes';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

export const SEGMENTS_856: Record<string, SegmentDefinition> = {
  BSN: {
    id: 'BSN',
    name: 'Beginning Segment for Ship Notice',
    purpose:
      'Identifies the shipment itself — its purpose, id/number, date and the hierarchical structure the HL loops below follow. Required at the start of every 856.',
    loop: LoopId.Header,
    required: true,
    elements: [
      el(
        1,
        'Transaction Set Purpose Code',
        'What this transmission of the ship notice is meant to do.',
        {
          dataType: 'code',
          required: true,
          codes: TRANSACTION_SET_PURPOSE_CODES,
        }
      ),
      el(
        2,
        'Shipment Identification',
        "The shipper's own shipment/ASN number.",
        {
          required: true,
        }
      ),
      el(3, 'Date', 'Date this ship notice was created.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
      el(4, 'Time', 'Time this ship notice was created.', {
        dataType: 'time',
        format: 'HHMM',
        required: true,
      }),
      el(
        5,
        'Hierarchical Structure Code',
        'Which levels the HL loops below use and in what order — governs how this transaction nests Shipment/Order/Pack/Tare/Item.',
        {
          dataType: 'code',
          codes: HIERARCHICAL_STRUCTURE_CODES,
        }
      ),
    ],
  },

  HL: {
    id: 'HL',
    name: 'Hierarchical Level',
    purpose:
      'Opens one level of the shipment hierarchy — Shipment, Order, Pack, Tare or Item — and states its own id, its parent’s id, and whether it has subordinate HL loops. The parser builds the Shipment→Order→Pack/Tare/Item tree from these three elements, not from a fixed nesting depth.',
    loop: LoopId.HL,
    required: true,
    elements: [
      el(
        1,
        'Hierarchical ID Number',
        'This level’s own id, unique within the transaction.',
        {
          required: true,
        }
      ),
      el(
        2,
        'Hierarchical Parent ID Number',
        'The id (HL01) of the level this one nests under. Absent for the top-level Shipment.'
      ),
      el(3, 'Hierarchical Level Code', 'Which kind of level this is.', {
        dataType: 'code',
        required: true,
        codes: HIERARCHICAL_LEVEL_CODES,
      }),
      el(
        4,
        'Hierarchical Child Code',
        'Whether at least one subordinate HL loop follows this one.',
        {
          dataType: 'code',
          codes: HIERARCHICAL_CHILD_CODES,
        }
      ),
    ],
  },

  TD1: {
    id: 'TD1',
    name: 'Carrier Details (Quantity and Weight)',
    purpose:
      'States how a Pack or Tare (or the shipment as a whole) is packaged, and its weight.',
    loop: LoopId.HL,
    elements: [
      el(
        1,
        'Packaging Code',
        'The physical packaging form this quantity is counted in.',
        {
          dataType: 'code',
          codes: PACKAGING_CODES,
        }
      ),
      el(2, 'Lading Quantity', 'The number of packages of that form.', {
        dataType: 'numeric',
      }),
      el(6, 'Weight Qualifier', 'What kind of weight follows.', {
        dataType: 'code',
        codes: WEIGHT_QUALIFIER_CODES,
      }),
      el(7, 'Weight', 'The weight value.', {dataType: 'numeric'}),
      el(
        8,
        'Unit or Basis for Measurement Code',
        'The unit the weight is expressed in.',
        {dataType: 'code', codes: UNIT_OR_BASIS_FOR_MEASUREMENT_CODES}
      ),
      el(9, 'Volume', 'The volume value.', {dataType: 'numeric'}),
    ],
  },

  TD5: {
    id: 'TD5',
    name: 'Carrier Details (Routing Sequence/Transit Time)',
    purpose: 'Identifies the carrier moving the shipment and how.',
    loop: LoopId.Header,
    elements: [
      el(
        1,
        'Routing Sequence Code',
        "Which party is responsible for routing at this point — the shipper's, the receiver's, or an intermediate carrier's."
      ),
      el(
        2,
        'Identification Code Qualifier',
        'How the carrier id is assigned.',
        {
          dataType: 'code',
          codes: IDENTIFICATION_CODE_QUALIFIER_CODES,
        }
      ),
      el(
        3,
        'Identification Code',
        'The carrier id, in the scheme named by element 2 — typically a SCAC.'
      ),
      el(4, 'Transportation Method/Type Code', 'How the shipment moves.', {
        dataType: 'code',
        codes: TRANSPORTATION_METHOD_CODES,
      }),
      el(5, 'Routing', 'Free-form routing description.'),
    ],
  },

  PRF: {
    id: 'PRF',
    name: 'Purchase Order Reference',
    purpose: 'Ties this Order level to the buyer’s own purchase order.',
    loop: LoopId.HL,
    elements: [
      el(1, 'Purchase Order Number', "The buyer's own PO number.", {
        required: true,
      }),
      el(
        2,
        'Release Number',
        'Release number against a blanket/standing order, when used.'
      ),
      el(
        3,
        'Change Order Sequence Number',
        'Which revision of the PO this refers to, when used.'
      ),
      el(4, 'Date', 'Date of the purchase order referenced.', {
        dataType: 'date',
        format: 'CCYYMMDD',
      }),
    ],
  },

  MAN: {
    id: 'MAN',
    name: 'Marks and Numbers',
    purpose:
      'Identifies a Pack or Tare by its shipping label/container marking.',
    loop: LoopId.HL,
    elements: [
      el(
        1,
        'Marks and Numbers Qualifier',
        'What kind of marking identifier follows.',
        {
          dataType: 'code',
          required: true,
          codes: MARKS_AND_NUMBERS_QUALIFIER_CODES,
        }
      ),
      el(2, 'Marks and Numbers', 'The marking/container identifier itself.', {
        required: true,
      }),
      el(
        3,
        'Marks and Numbers Qualifier',
        'What kind of marking identifier follows, for a second marking.',
        {dataType: 'code', codes: MARKS_AND_NUMBERS_QUALIFIER_CODES}
      ),
      el(4, 'Marks and Numbers', 'The second marking/container identifier.'),
    ],
  },

  SN1: {
    id: 'SN1',
    name: 'Item Detail (Shipment)',
    purpose: 'States how much of this Item is in the shipment.',
    loop: LoopId.HL,
    required: true,
    elements: [
      el(
        1,
        'Assigned Identification',
        'The line number, assigned by the shipper.'
      ),
      el(2, 'Number of Units Shipped', 'The quantity of this item shipped.', {
        dataType: 'numeric',
        required: true,
      }),
      el(
        3,
        'Unit or Basis for Measurement Code',
        'The unit the shipped quantity is expressed in.',
        {dataType: 'code', required: true, codes: UNIT_OR_BASIS_FOR_MEASUREMENT_CODES}
      ),
      el(
        4,
        'Quantity Ordered',
        'The quantity of this item originally ordered.',
        {
          dataType: 'numeric',
        }
      ),
      el(
        8,
        'Line Item Status Code',
        'Whether this item shipped as ordered, was substituted, or was backordered/deleted.',
        {
          dataType: 'code',
          codes: LINE_ITEM_STATUS_CODES,
        }
      ),
    ],
  },
};
